import {
  CreateI, DeleteI, PageI, QueryI, UpdateI,
} from '@/interface/basicOperations';
import { BasicUser, DisableUser } from '@/interface/basicUser';
import axios from '@/axios/api';
import { definitions } from '@/types/api';
import {
  computed, ComputedRef, reactive, ref, toRefs,
} from 'vue';
import { message } from 'ant-design-vue';
import { MessageApi } from 'ant-design-vue/lib/message';
import validate from '@/lib/validate';
import userServices from '@/services/user.services';
import { promiseConfirm } from '@/lib/message';
import { editFormRules } from './teacherModule';
import {
  ClassInfoI, ClassMemberColumnI, ClassMemberCreateRequestI, Key,
} from './interface';

const { delUserByIds } = userServices();

const createClassMemberService = async (reqData: definitions['CreateOrUpdateClassMemberRequest']): Promise<definitions['ClassMember']> => {
  try {
    const { data } = (await axios.createClassMember(reqData)).data;
    return data;
  } catch (err) {
    throw new Error(err);
  }
};

const queryClassMemberListService = async (reqData: definitions['ListClassMemberRequest']): Promise<definitions['PageResponse«ClassMember»']> => {
  try {
    const { data } = (await axios.queryStudentList(reqData)).data;
    return data;
  } catch (err) {
    throw new Error(err);
  }
};

export interface ClassMemberI extends CreateI<ClassMemberCreateRequestI, BasicUser>, DeleteI<DisableUser, boolean>, UpdateI<BasicUser, BasicUser>, QueryI<string, void> {
  list: BasicUser[];
  totalRecords: number;
  className: string;
  classId: string;
  /**
   * 重置密码
   */
   resetPassword(reqData: {code: string}): Promise<void>;

   /**
    * 设置班级名称
    * @param name {string}
    */
   setClassName(name: string): void;
}

export class ClassMember implements ClassMemberI {
  classId = '';

  className = ''

  list: BasicUser[] = [];

  totalRecords = 0;

  class: ClassInfoI = {} as ClassInfoI

  setClassName(name: string): void {
    this.className = name;
  }

  setClassId(id: string): void {
    this.classId = id;
  }

  setClass(classItem: ClassInfoI): void {
    this.class = classItem;
  }

  // eslint-disable-next-line class-methods-use-this
  async resetPassword(reqData: { code: string }): Promise<void> {
    try {
      await axios.adminResetPassword(reqData);
    } catch (err) {
      throw new Error(err);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async create(reqData: {name: string; userId: string}): Promise<BasicUser> {
    try {
      const member = await createClassMemberService({
        ...reqData,
        classId: this.classId,
      });
      return {
        name: member.name || '',
        code: member.userId || '',
      };
    } catch (err) {
      throw new Error(err);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async delete(reqData: DisableUser): Promise<boolean> {
    try {
      console.log(reqData);
      return true;
    } catch (err) {
      throw new Error(err);
    }
  }

  async update(reqData: BasicUser): Promise<BasicUser> {
    try {
      await axios.changeUserSignature(reqData);
      const target = this.list.find((t) => t.code === reqData.code);
      return target as BasicUser;
    } catch (err) {
      throw new Error(err);
    }
  }

  // eslint-disable-next-line @typescript-eslint/camelcase
  async query(page: PageI, classRoom_uuid: string = this.classId): Promise<void> {
    try {
      // eslint-disable-next-line @typescript-eslint/camelcase
      const { list, totalRecords } = await queryClassMemberListService({ ...page, classRoom_uuid });
      if (Array.isArray(list)) {
        this.list = list.map((s) => ({
          name: s.name || '',
          code: s.userId || '',
        }));
        this.totalRecords = totalRecords || 0;
      } else {
        this.list = [];
        this.totalRecords = 0;
      }
    } catch (err) {
      throw new Error(err);
    }
  }
}

export default (Message: MessageApi = message) => {
  const classMemberModule = reactive(new ClassMember());

  const showValidate = ref(false);

  const pagination = reactive({
    pageSize: 12,
    pageNo: 1,
  });

  const columns = [
    {
      title: '序号',
      dataIndex: 'index',
      width: 120,
      key: 'index',
    },
    {
      title: '学生姓名',
      dataIndex: 'name',
      ellipsis: true,
      key: 'name',
    },
    {
      title: '账号',
      dataIndex: 'code',
      ellipsis: true,
      key: 'code',
    },
    {
      title: '操作',
      dataIndex: 'operation',
      align: 'center',
      slots: { customRender: 'operation' },
    },
  ];

  const dataSource: ComputedRef<ClassMemberColumnI[]> = computed((): ClassMemberColumnI[] => classMemberModule.list.map((t, index): ClassMemberColumnI => ({
    key: t.code,
    index: (pagination.pageNo - 1) * pagination.pageSize + index + 1,
    name: t.name,
    code: t.code,
  })));

  const selectState = reactive<{
    selectedRowKeys: Key[];
    loading: boolean;
  }>({
    selectedRowKeys: [],
    loading: false,
  });

  const hasSelected = computed(() => selectState.selectedRowKeys.length > 0);

  const modalVisible = reactive({
    edit: false,
    reset: false,
    plus: false,
  });

  const modalConfirmLoading = reactive({
    edit: false,
    reset: false,
    plus: false,
  });

  const editForm: BasicUser = reactive({
    name: '',
    code: '',
  });

  const query = async (pageSize: number = pagination.pageSize, pageNo: number = pagination.pageNo) => {
    try {
      await classMemberModule.query({
        pageNo,
        pageSize,
      });
    } catch (err) {
      throw new Error(err);
    }
  };

  const handlePageChange = async () => {
    try {
      await classMemberModule.query(pagination);
    } catch (err) {
      throw new Error(err);
    }
  };

  const handleEditClick = (t: ClassMemberColumnI) => {
    editForm.code = t.code;
    editForm.name = t.name;
    modalVisible.edit = true;
  };

  const handleResetPasswordClick = (t: ClassMemberColumnI) => {
    editForm.code = t.code;
    editForm.name = t.name;
    modalVisible.reset = true;
  };

  const handleDeleteClick = async () => {
    try {
      // Message.info('删除功能暂未开放');
      promiseConfirm('将删除学生账号及所有实验数据，此操作不可逆，是否确认删除？', async () => {
        selectState.loading = true;
        await delUserByIds(classMemberModule.classId, selectState.selectedRowKeys as string[]);
        Message.success('删除成功!');
        await query();
        selectState.loading = false;
      }, undefined, () => {
        selectState.loading = false;
      });
    } catch (err) {
      throw new Error(err);
    }
  };

  const handlePlusClick = () => {
    modalVisible.plus = true;
  };

  const handleEditCancel = () => {
    editForm.code = '';
    editForm.name = '';
    modalVisible.edit = false;
  };

  const handleResetPasswordCancel = () => {
    handleEditCancel();
    modalVisible.reset = false;
  };

  const handlePlusCancel = () => {
    editForm.code = '';
    editForm.name = '';
    modalVisible.plus = false;
  };

  const handleDeleteCancel = () => {
    // modalVisible.
    console.log(1);
  };

  const handleEditOk = async () => {
    try {
      validate(editForm, editFormRules);
      modalConfirmLoading.edit = true;
      await classMemberModule.update({
        name: editForm.name,
        code: editForm.code,
      });
      message.success('修改成功');
      await query();
      handleEditCancel();
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.edit = false;
    }
  };

  const handleResetPasswordOk = async () => {
    try {
      modalConfirmLoading.reset = true;
      await classMemberModule.resetPassword({ code: editForm.code });
      message.success('重置密码成功');
      handleResetPasswordCancel();
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.reset = false;
    }
  };

  const handlePlusOk = async () => {
    try {
      editForm.name = editForm.name.trim();
      validate(editForm, editFormRules);
      modalConfirmLoading.plus = true;
      await classMemberModule.create({
        name: editForm.name,
        userId: editForm.code,
      });
      // eslint-disable-next-line no-plusplus
      classMemberModule.class.stuNum++;
      message.success('添加成功');
      await query();
      modalConfirmLoading.plus = false;
      handlePlusCancel();
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.plus = false;
    }
  };

  const handleSelectedRowsChange = (selectedRowKeys: Key[]) => {
    selectState.selectedRowKeys = selectedRowKeys;
  };

  return {
    classMemberModule,
    pagination,
    showValidate,
    columns,
    dataSource,
    hasSelected,
    editForm,
    modalVisible,
    modalConfirmLoading,
    ...toRefs(selectState),
    query,
    handlePageChange,
    handleSelectedRowsChange,
    handleDeleteClick,
    handleEditClick,
    handleResetPasswordClick,
    handlePlusClick,
    handleEditOk,
    handleResetPasswordOk,
    handlePlusOk,
    handleEditCancel,
    handleDeleteCancel,
    handleResetPasswordCancel,
    handlePlusCancel,
  };
};
